<script setup lang="ts">
/**
 * Дерево страниц в сайдбаре — макет 22:2: строка 28, активный узел на подложке.
 *
 * Второго сайдбара в разделе нет: дерево занимает место списка проектов. Узел
 * с детьми раскрывается стрелкой, сама строка всегда ведёт на страницу —
 * раскрытие и переход это два разных намерения.
 */
import { ChevronDown, ChevronRight, FileText } from 'lucide-vue-next'
import { ref } from 'vue'

import type { PageNode } from '../api'

defineProps<{ nodes: PageNode[]; activeId: string; depth?: number }>()

const collapsed = ref(new Set<string>())

const toggle = (id: string) => {
  const next = new Set(collapsed.value)

  if (next.has(id)) next.delete(id)
  else next.add(id)

  collapsed.value = next
}
</script>

<template>
  <div class="kbtree">
    <template v-for="node in nodes" :key="node.id">
      <div class="kbtree__row" :class="{ 'kbtree__row--active': node.id === activeId }">
        <button
          v-if="node.children.length"
          type="button"
          class="kbtree__toggle"
          :aria-expanded="!collapsed.has(node.id)"
          :aria-label="node.title"
          @click="toggle(node.id)"
        >
          <ChevronDown v-if="!collapsed.has(node.id)" :size="12" />
          <ChevronRight v-else :size="12" />
        </button>
        <span v-else class="kbtree__toggle kbtree__toggle--empty" />

        <RouterLink class="kbtree__link" :to="`/kb/${node.id}`">
          <FileText :size="14" />
          <span class="kbtree__title">{{ node.title }}</span>
        </RouterLink>
      </div>

      <KbTree
        v-if="node.children.length && !collapsed.has(node.id)"
        class="kbtree--nested"
        :nodes="node.children"
        :active-id="activeId"
      />
    </template>
  </div>
</template>

<style scoped>
.kbtree {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Вложенный уровень отступает на 14 — как в макете; глубже трёх уровней
   страницы обычно не уходят, и отступ остаётся читаемым. */
.kbtree--nested {
  padding-left: 14px;
}

.kbtree__row {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  border-radius: var(--radius-md);
}

.kbtree__row:hover {
  background: var(--color-bg-muted);
}

.kbtree__row--active {
  background: var(--color-primary-tint);
}

.kbtree__row--active .kbtree__link {
  color: var(--color-primary-hover);
  font-weight: 500;
}

.kbtree__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.kbtree__toggle--empty {
  cursor: default;
}

.kbtree__link {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0 8px 0 2px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  text-decoration: none;
}

.kbtree__title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
